import React, { useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";

import { crmPostventaApi } from "./crmPostventaApi";

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;
const MOBILE_CARD_BREAKPOINT = 1100;
const RESULT_LABELS = {
  Respondio: "Respondio",
  "No respondio": "No respondio",
  "Solicita rollos": "Solicita rollos",
  "Consulta comercial": "Consulta comercial",
  "Consulta sobre acreditacion": "Consulta sobre acreditacion",
  "Problema tecnico": "Problema tecnico",
  "Solicitud de baja": "Solicitud de baja",
  "Datos incorrectos": "Datos incorrectos",
  Otro: "Otro",
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const formatDateTime = (value) => {
  if (!value || value === "-") return "-";

  const raw = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}( \d{2}:\d{2})?$/.test(raw)) return raw;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const dateOnly = new Date(`${raw}T00:00:00`);
    return Number.isNaN(dateOnly.getTime())
      ? raw
      : dateOnly.toLocaleDateString("es-AR");
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;

  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const splitDateTime = (value) => {
  const formatted = formatDateTime(value);
  if (formatted === "-" || !formatted.includes(" ")) {
    return { date: formatted, time: "" };
  }

  const [date, time] = formatted.split(" ");
  return { date, time: time || "" };
};

const toLocalDateInputValue = (date = new Date()) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  const offset = value.getTimezoneOffset() * 60000;
  return new Date(value.getTime() - offset).toISOString().slice(0, 10);
};

const normalizeDigits = (value) => String(value || "").replace(/\D/g, "");
const createCorrelationId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `zoco-check-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const normalizeWhatsappPhone = (value) => {
  const digits = normalizeDigits(value);
  if (!digits) return "";
  if (digits.startsWith("549")) return digits;
  if (digits.startsWith("54")) return `549${digits.slice(2)}`;
  if (digits.startsWith("0")) return `549${digits.slice(1)}`;
  return `549${digits}`;
};

const copyText = async (text, title, successMessage = "Texto copiado correctamente.") => {
  try {
    await navigator.clipboard.writeText(text || "");
    Swal.fire(title, successMessage, "success");
  } catch {
    Swal.fire(title, "No se pudo copiar el texto.", "error");
  }
};

const buildAttemptLabel = (attempt) => `Intento ${Math.max(1, Number(attempt || 1))} de 3`;

const mapStateLabel = (state) => {
  const normalized = String(state || "").trim().toLowerCase();
  if (normalized.includes("pendiente_primer_contacto")) return "Primer WhatsApp";
  if (normalized.includes("esperando_segundo_whatsapp")) return "Segundo WhatsApp";
  if (normalized.includes("esperando_llamada")) return "Llamada";
  if (normalized.includes("sin_respuesta")) return "Sin respuesta";
  if (normalized.includes("cerrado")) return "Cerrado";
  if (normalized.includes("reclasificado")) return "Reclasificado";
  return state || "-";
};

const toneForPriority = (priority) => {
  const normalized = String(priority || "").toLowerCase();
  if (normalized.includes("cr")) return "danger";
  if (normalized.includes("alta")) return "warning";
  if (normalized.includes("media")) return "default";
  return "success";
};

const toneForState = (state) => {
  const normalized = String(state || "").toLowerCase();
  if (normalized.includes("cerrado")) return "default";
  if (normalized.includes("respuesta")) return "danger";
  if (normalized.includes("llamada")) return "warning";
  return "success";
};

const parseErrorMessage = (error) => error?.message || "No se pudo completar la operacion.";

const normalizeOperativoItem = (item = {}) => ({
  flujoId: Number(item?.flujoId || item?.FlujoId || 0),
  clienteId: Number(item?.clienteId || item?.ClienteId || 0) || null,
  seguimientoPrincipalId: Number(item?.seguimientoPrincipalId || item?.SeguimientoPrincipalId || 0) || null,
  procesoCodigo: item?.procesoCodigo || item?.ProcesoCodigo || "",
  entidadTipo: item?.entidadTipo || item?.EntidadTipo || "",
  entidadKey: item?.entidadKey || item?.EntidadKey || "",
  cuit: item?.cuit || item?.Cuit || "",
  comercio: item?.comercio || item?.Comercio || "",
  numeroComercio: item?.numeroComercio || item?.NumeroComercio || "",
  terminal: item?.terminal || item?.Terminal || "",
  telefono: item?.telefono || item?.Telefono || "",
  prioridad: item?.prioridad || item?.Prioridad || "",
  estadoFlujo: item?.estadoFlujo || item?.EstadoFlujo || "",
  intentoActual: Number(item?.intentoActual || item?.IntentoActual || 1),
  canalPermitido: item?.canalPermitido || item?.CanalPermitido || "",
  accionPermitida: item?.accionPermitida || item?.AccionPermitida || "",
  mensajeSugerido: item?.mensajeSugerido || item?.MensajeSugerido || "",
  requiereEvidencia: Boolean(item?.requiereEvidencia ?? item?.RequiereEvidencia),
  requiereObservacion: Boolean(item?.requiereObservacion ?? item?.RequiereObservacion),
  puedeCerrar: Boolean(item?.puedeCerrar ?? item?.PuedeCerrar),
  puedeDerivar: Boolean(item?.puedeDerivar ?? item?.PuedeDerivar),
  fechaProximoContacto: item?.fechaProximoContacto || item?.FechaProximoContacto || "-",
  esLegacy: Boolean(item?.esLegacy ?? item?.EsLegacy),
  versionToken: item?.versionToken || item?.VersionToken || "",
  resultadosPermitidos: Array.isArray(item?.resultadosPermitidos || item?.ResultadosPermitidos)
    ? (item?.resultadosPermitidos || item?.ResultadosPermitidos)
    : [],
  canalesPermitidos: Array.isArray(item?.canalesPermitidos || item?.CanalesPermitidos)
    ? (item?.canalesPermitidos || item?.CanalesPermitidos)
    : [],
});

const normalizeHistorialItem = (item = {}) => ({
  id: Number(item?.id || item?.Id || 0),
  fechaHora: item?.fechaHora || item?.FechaHora || "-",
  usuario: item?.usuario || item?.Usuario || "-",
  estado: item?.estado || item?.Estado || "-",
  observacion: item?.observacion || item?.Observacion || "-",
  canalContacto: item?.canalContacto || item?.CanalContacto || "-",
  evidenciaContacto: item?.evidenciaContacto || item?.EvidenciaContacto || "-",
  intentoContacto: item?.intentoContacto || item?.IntentoContacto || null,
  resultadoLlamada: item?.resultadoLlamada || item?.ResultadoLlamada || "-",
});

const ZocoCheckPanel = ({ darkMode, rows = [], isAdmin = false, onRefresh }) => {
  const [filters, setFilters] = useState({
    search: "",
    priority: "",
    state: "",
    attempt: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [operativos, setOperativos] = useState([]);
  const [detailItem, setDetailItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isCompactView, setIsCompactView] = useState(() => (typeof window !== "undefined" ? window.innerWidth < MOBILE_CARD_BREAKPOINT : false));
  const [form, setForm] = useState({
    resultado: "",
    observacion: "",
    evidencia: "",
    duracionSegundos: "",
    correlationId: "",
  });
  const scrollRef = useRef(null);

  const reloadOperativos = async () => {
    setLoading(true);
    try {
      const data = await crmPostventaApi.obtenerZocoCheckOperativos();
      const filas = Array.isArray(data?.filas || data?.Filas) ? (data?.filas || data?.Filas) : [];
      setOperativos(filas.map(normalizeOperativoItem));
      setErrorMessage("");
    } catch (error) {
      setOperativos([]);
      setErrorMessage(parseErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadOperativos();
  }, [Array.isArray(rows) ? rows.length : 0]);

  useEffect(() => {
    const updateViewport = () => setIsCompactView(window.innerWidth < MOBILE_CARD_BREAKPOINT);
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, [page, pageSize, operativos.length]);

  const priorityOptions = useMemo(() => Array.from(new Set(operativos.map((item) => item.prioridad).filter(Boolean))).sort(), [operativos]);
  const stateOptions = useMemo(() => Array.from(new Set(operativos.map((item) => mapStateLabel(item.estadoFlujo)).filter(Boolean))).sort(), [operativos]);

  const filteredRows = useMemo(() => {
    const needle = String(filters.search || "").trim().toLowerCase();

    return operativos.filter((item) => {
      if (filters.priority && item.prioridad !== filters.priority) return false;
      if (filters.state && mapStateLabel(item.estadoFlujo) !== filters.state) return false;
      if (filters.attempt && String(item.intentoActual) !== filters.attempt) return false;
      if (!needle) return true;

      return [
        item.comercio,
        item.cuit,
        item.numeroComercio,
        item.terminal,
        item.telefono,
        item.prioridad,
        item.estadoFlujo,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [filters, operativos]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const pageRows = useMemo(() => filteredRows.slice((page - 1) * pageSize, page * pageSize), [filteredRows, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [filters, pageSize]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const clearFilters = () => {
    setFilters({
      search: "",
      priority: "",
      state: "",
      attempt: "",
    });
  };

  const loadDetail = async (flujoId) => {
    setDetailLoading(true);
    try {
      const data = await crmPostventaApi.obtenerZocoCheckOperativo(flujoId);
      return {
        flujo: normalizeOperativoItem(data?.flujo || data?.Flujo || {}),
        historial: Array.isArray(data?.historial || data?.Historial)
          ? (data?.historial || data?.Historial).map(normalizeHistorialItem)
          : [],
      };
    } finally {
      setDetailLoading(false);
    }
  };

  const openDetailModal = async (item) => {
    try {
      const detail = await loadDetail(item.flujoId);
      setDetailItem(detail);
    } catch (error) {
      Swal.fire("Zoco Check", parseErrorMessage(error), "error");
    }
  };

  const openGestionModal = async (item) => {
    if (item.esLegacy) {
      Swal.fire("Zoco Check", "El registro es legacy y solo puede verse desde historial.", "info");
      return;
    }

    try {
      const detail = await loadDetail(item.flujoId);
      setActiveItem(detail);
      setForm({
        resultado: "",
        observacion: "",
        evidencia: "",
        duracionSegundos: "",
        correlationId: createCorrelationId(),
      });
    } catch (error) {
      Swal.fire("Zoco Check", parseErrorMessage(error), "error");
    }
  };

  const closeGestionModal = () => {
    setActiveItem(null);
  };

  const handleBootstrap = async () => {
    setBootstrapping(true);
    try {
      const data = await crmPostventaApi.bootstrapZocoCheck(toLocalDateInputValue());
      const warnings = Array.isArray(data?.warnings) && data.warnings.length ? `<br/><br/>Advertencias:<br/>${data.warnings.join("<br/>")}` : "";
      await Swal.fire({
        title: "Bootstrap Zoco Check",
        html: `${data?.message || "Proceso ejecutado."}${warnings}`,
        icon: Number(data?.flujosCreados ?? 0) > 0 ? "success" : "info",
      });

      await reloadOperativos();
      if (typeof onRefresh === "function") {
        await onRefresh();
      }
    } catch (error) {
      Swal.fire("Bootstrap", parseErrorMessage(error), "error");
    } finally {
      setBootstrapping(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const flujo = activeItem?.flujo;
    if (!flujo?.flujoId) {
      Swal.fire("Zoco Check", "No se encontro el flujo operativo.", "error");
      return;
    }

    if (!form.resultado) {
      Swal.fire("Zoco Check", "Debes seleccionar un resultado.", "info");
      return;
    }

    if (flujo.requiereObservacion && !String(form.observacion || "").trim()) {
      Swal.fire("Zoco Check", "Debes completar una observacion.", "info");
      return;
    }

    if (flujo.requiereEvidencia && !String(form.evidencia || "").trim()) {
      Swal.fire("Zoco Check", "Debes completar la evidencia obligatoria.", "info");
      return;
    }

    const duracionSegundos = String(form.duracionSegundos || "").trim();
    if (duracionSegundos && Number.isNaN(Number(duracionSegundos))) {
      Swal.fire("Zoco Check", "La duracion debe ser numerica.", "info");
      return;
    }

    setSaving(true);
    try {
      const response = await crmPostventaApi.ejecutarZocoCheckAccion(flujo.flujoId, {
        Accion: flujo.accionPermitida || "CONTACTAR",
        Canal: flujo.canalPermitido,
        Resultado: form.resultado,
        Observacion: form.observacion,
        Evidencia: form.evidencia || null,
        DuracionSegundos: duracionSegundos ? Number(duracionSegundos) : null,
        VersionToken: flujo.versionToken,
        CorrelationId: form.correlationId,
      });

      await Swal.fire({
        title: "Zoco Check",
        html: `${response?.message || "Accion registrada correctamente."}${response?.transicionAplicada ? `<br/><br/>Transicion: ${response.transicionAplicada}` : ""}`,
        icon: "success",
      });

      closeGestionModal();
      await reloadOperativos();
      if (typeof onRefresh === "function") {
        await onRefresh();
      }
    } catch (error) {
      Swal.fire("Zoco Check", parseErrorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  };

  const renderFilters = () => (
    <div className="crm-postventa-zoco-filters-wrap">
      <div className="crm-postventa-zoco-filter-grid crm-postventa-zoco-filter-grid-top">
        <Form.Control
          type="search"
          className="crm-postventa-filter-control"
          placeholder="Buscar por comercio, CUIT, comercio, terminal o telefono"
          value={filters.search}
          onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
        />
        <Form.Select className="crm-postventa-filter-control" value={filters.priority} onChange={(event) => setFilters((prev) => ({ ...prev, priority: event.target.value }))}>
          <option value="">Todas las prioridades</option>
          {priorityOptions.map((item) => <option key={item} value={item}>{item}</option>)}
        </Form.Select>
        <Form.Select className="crm-postventa-filter-control" value={filters.state} onChange={(event) => setFilters((prev) => ({ ...prev, state: event.target.value }))}>
          <option value="">Todos los estados</option>
          {stateOptions.map((item) => <option key={item} value={item}>{item}</option>)}
        </Form.Select>
        <Form.Select className="crm-postventa-filter-control" value={filters.attempt} onChange={(event) => setFilters((prev) => ({ ...prev, attempt: event.target.value }))}>
          <option value="">Todos los intentos</option>
          <option value="1">Intento 1</option>
          <option value="2">Intento 2</option>
          <option value="3">Intento 3</option>
        </Form.Select>
      </div>
      <div className="crm-postventa-zoco-filter-grid crm-postventa-zoco-filter-grid-bottom">
        <div className="crm-postventa-zoco-filter-actions">
          <Button variant="outline-secondary" onClick={clearFilters}>Limpiar filtros</Button>
          <Button variant="outline-secondary" onClick={reloadOperativos} disabled={loading}>
            {loading ? "Actualizando..." : "Actualizar"}
          </Button>
        </div>
        <div className="crm-postventa-zoco-filter-actions crm-postventa-zoco-filter-actions-end">
          {isAdmin ? (
            <Button variant="success" onClick={handleBootstrap} disabled={bootstrapping}>
              {bootstrapping ? "Generando..." : "Generar Zoco Check del mes"}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );

  const renderActions = (item) => {
    const whatsapp = normalizeWhatsappPhone(item.telefono);
    return (
      <div className="crm-postventa-zoco-actions">
        <Button size="sm" variant="outline-secondary" onClick={() => openDetailModal(item)}>
          Detalle
        </Button>
        {whatsapp ? (
          <Button size="sm" variant="outline-success" href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer">
            WhatsApp
          </Button>
        ) : (
          <Button size="sm" variant="outline-secondary" disabled>
            WhatsApp
          </Button>
        )}
        <Button size="sm" variant="success" onClick={() => openGestionModal(item)} disabled={item.esLegacy}>
          Gestionar
        </Button>
      </div>
    );
  };

  const renderDesktopTable = () => (
    <div className="crm-postventa-zoco-table-shell">
      <div ref={scrollRef} className="crm-postventa-zoco-table-scroll">
        <Table className="crm-postventa-table crm-postventa-zoco-compact-table mb-0" hover>
          <thead>
            <tr>
              <th>Prioridad</th>
              <th>Comercio</th>
              <th>CUIT</th>
              <th>Nro comercio</th>
              <th>Terminal</th>
              <th>Intento</th>
              <th>Estado</th>
              <th>Canal</th>
              <th>Proximo contacto</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length ? pageRows.map((item) => {
              const nextContact = splitDateTime(item.fechaProximoContacto);
              const visualState = mapStateLabel(item.estadoFlujo);
              return (
                <tr key={item.flujoId}>
                  <td className="crm-postventa-cell-nowrap">
                    <span className={`crm-postventa-badge tone-${toneForPriority(item.prioridad)}`}>{item.prioridad || "-"}</span>
                  </td>
                  <td className="crm-postventa-zoco-main-cell">
                    <strong title={item.comercio || "-"}>{item.comercio || "-"}</strong>
                    <span title={item.telefono || "-"}>{item.telefono || "-"}</span>
                  </td>
                  <td className="crm-postventa-cell-nowrap">{item.cuit || "-"}</td>
                  <td className="crm-postventa-cell-nowrap">{item.numeroComercio || "-"}</td>
                  <td className="crm-postventa-cell-nowrap">{item.terminal || "-"}</td>
                  <td className="crm-postventa-cell-nowrap">{buildAttemptLabel(item.intentoActual)}</td>
                  <td className="crm-postventa-cell-nowrap">
                    <span className={`crm-postventa-badge crm-postventa-zoco-state-badge tone-${toneForState(visualState)}`}>
                      {visualState}
                    </span>
                  </td>
                  <td className="crm-postventa-cell-nowrap">{item.canalPermitido || "-"}</td>
                  <td className="crm-postventa-cell-nowrap">
                    <div className="crm-postventa-zoco-date-cell" title={formatDateTime(item.fechaProximoContacto)}>
                      <span>{nextContact.date || "-"}</span>
                      {nextContact.time ? <small>{nextContact.time}</small> : null}
                    </div>
                  </td>
                  <td>{renderActions(item)}</td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={10} className="text-center py-4 text-muted">
                  No hay Zoco Check operativos para los filtros actuales.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );

  const renderCompactCards = () => (
    <div className="crm-postventa-zoco-card-list">
      {pageRows.length ? pageRows.map((item) => {
        const nextContact = splitDateTime(item.fechaProximoContacto);
        const visualState = mapStateLabel(item.estadoFlujo);
        return (
          <article key={item.flujoId} className="crm-postventa-zoco-card">
            <div className="crm-postventa-zoco-card-head">
              <div>
                <strong title={item.comercio || "-"}>{item.comercio || "-"}</strong>
                <span>{item.telefono || "-"}</span>
              </div>
              <span className={`crm-postventa-badge tone-${toneForPriority(item.prioridad)}`}>{item.prioridad || "-"}</span>
            </div>
            <div className="crm-postventa-zoco-card-meta">
              <div>
                <span className="crm-postventa-card-label">Estado</span>
                <strong>{visualState}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Canal</span>
                <strong>{item.canalPermitido || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Proximo contacto</span>
                <strong>{nextContact.date || "-"}</strong>
                {nextContact.time ? <small>{nextContact.time}</small> : null}
              </div>
            </div>
            <div className="crm-postventa-zoco-card-actions">
              {renderActions(item)}
            </div>
          </article>
        );
      }) : (
        <div className="text-center py-4 text-muted">No hay Zoco Check operativos para los filtros actuales.</div>
      )}
    </div>
  );

  const renderPagination = () => (
    <div className="crm-postventa-zoco-footer">
      <div className="crm-postventa-zoco-footer-meta">
        <Form.Select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
          {PAGE_SIZE_OPTIONS.map((item) => <option key={item} value={item}>{item} por pagina</option>)}
        </Form.Select>
      </div>
      <div className="crm-postventa-zoco-footer-pagination">
        <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>
          Anterior
        </Button>
        <span>Pagina {page} de {totalPages}</span>
        <Button size="sm" variant="outline-secondary" disabled={page >= totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}>
          Siguiente
        </Button>
      </div>
    </div>
  );

  const detailFlujo = detailItem?.flujo || null;
  const activeFlujo = activeItem?.flujo || null;
  const activeHistory = activeItem?.historial || [];
  const activeWhatsappPhone = normalizeWhatsappPhone(activeFlujo?.telefono);
  const allowedResults = Array.isArray(activeFlujo?.resultadosPermitidos) ? activeFlujo.resultadosPermitidos : [];

  return (
    <section className={`crm-postventa-panel crm-postventa-alert-queue ${darkMode ? "is-dark" : ""}`}>
      <div className="crm-postventa-panel-title">
        <div>
          <h3>Zoco Check</h3>
          <span className="crm-postventa-card-label">
            Mostrando {pageRows.length} de {filteredRows.length} filas operativas
          </span>
        </div>
      </div>

      {renderFilters()}

      {errorMessage ? <Alert variant="danger">{errorMessage}</Alert> : null}
      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          Cargando cola operativa...
        </div>
      ) : isCompactView ? renderCompactCards() : renderDesktopTable()}

      {renderPagination()}

      <Modal show={Boolean(detailItem)} onHide={() => setDetailItem(null)} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Detalle Zoco Check · {detailFlujo?.comercio || "-"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-grid gap-3">
          {detailLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Cargando detalle...
            </div>
          ) : (
            <>
              <div className="crm-postventa-zoco-detail-grid">
                <div><span className="crm-postventa-card-label">Flujo</span><strong>#{detailFlujo?.flujoId || "-"}</strong></div>
                <div><span className="crm-postventa-card-label">Estado</span><strong>{mapStateLabel(detailFlujo?.estadoFlujo)}</strong></div>
                <div><span className="crm-postventa-card-label">Intento</span><strong>{buildAttemptLabel(detailFlujo?.intentoActual)}</strong></div>
                <div><span className="crm-postventa-card-label">Canal permitido</span><strong>{detailFlujo?.canalPermitido || "-"}</strong></div>
                <div><span className="crm-postventa-card-label">Accion permitida</span><strong>{detailFlujo?.accionPermitida || "-"}</strong></div>
                <div><span className="crm-postventa-card-label">Proximo contacto</span><strong>{formatDateTime(detailFlujo?.fechaProximoContacto)}</strong></div>
                <div><span className="crm-postventa-card-label">Puede cerrar</span><strong>{detailFlujo?.puedeCerrar ? "Si" : "No"}</strong></div>
                <div><span className="crm-postventa-card-label">Puede derivar</span><strong>{detailFlujo?.puedeDerivar ? "Si" : "No"}</strong></div>
                <div className="crm-postventa-zoco-detail-wide">
                  <span className="crm-postventa-card-label">Mensaje sugerido</span>
                  <div className="crm-postventa-detail-pre">{detailFlujo?.mensajeSugerido || "-"}</div>
                </div>
              </div>

              <div className="crm-postventa-zoco-detail-wide">
                <span className="crm-postventa-card-label">Historial</span>
                {detailItem?.historial?.length ? (
                  <div className="crm-postventa-zoco-table-shell">
                    <Table className="crm-postventa-table mb-0" hover>
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Usuario</th>
                          <th>Estado</th>
                          <th>Canal</th>
                          <th>Intento</th>
                          <th>Observacion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailItem.historial.map((row) => (
                          <tr key={row.id}>
                            <td>{formatDateTime(row.fechaHora)}</td>
                            <td>{row.usuario || "-"}</td>
                            <td>{row.estado || "-"}</td>
                            <td>{row.canalContacto || "-"}</td>
                            <td>{row.intentoContacto ? buildAttemptLabel(row.intentoContacto) : "-"}</td>
                            <td>{row.observacion || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert variant="light" className="mb-0">No hay historial operativo registrado.</Alert>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDetailItem(null)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(activeItem)} onHide={closeGestionModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Zoco Check · {activeFlujo?.comercio || "-"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body className="d-grid gap-3">
            <div className="crm-postventa-zoco-modal-grid">
              <div><span className="crm-postventa-card-label">Flujo</span><strong>#{activeFlujo?.flujoId || "-"}</strong></div>
              <div><span className="crm-postventa-card-label">CUIT</span><strong>{activeFlujo?.cuit || "-"}</strong></div>
              <div><span className="crm-postventa-card-label">Comercio</span><strong>{activeFlujo?.numeroComercio || "-"}</strong></div>
              <div><span className="crm-postventa-card-label">Terminal</span><strong>{activeFlujo?.terminal || "-"}</strong></div>
              <div><span className="crm-postventa-card-label">Canal permitido</span><strong>{activeFlujo?.canalPermitido || "-"}</strong></div>
              <div><span className="crm-postventa-card-label">Intento</span><strong>{buildAttemptLabel(activeFlujo?.intentoActual)}</strong></div>
            </div>

            <Alert variant="light" className="mb-0">
              <div><strong>Reglas del motor</strong></div>
              <div>Accion permitida: {activeFlujo?.accionPermitida || "-"}</div>
              <div>Requiere evidencia: {activeFlujo?.requiereEvidencia ? "Si" : "No"}</div>
              <div>Requiere observacion: {activeFlujo?.requiereObservacion ? "Si" : "No"}</div>
              <div>Resultados habilitados: {allowedResults.length ? allowedResults.join(", ") : "-"}</div>
            </Alert>

            <div className="crm-postventa-zoco-message">
              <span className="crm-postventa-card-label">Mensaje sugerido</span>
              <div className="crm-postventa-detail-pre">{activeFlujo?.mensajeSugerido || "-"}</div>
            </div>

            <div className="crm-postventa-zoco-cta-row">
              {activeFlujo?.mensajeSugerido ? (
                <Button type="button" variant="outline-secondary" onClick={() => copyText(activeFlujo.mensajeSugerido, "Zoco Check")}>
                  Copiar mensaje
                </Button>
              ) : null}
              {activeWhatsappPhone ? (
                <Button type="button" variant="outline-success" href={`https://wa.me/${activeWhatsappPhone}`} target="_blank" rel="noopener noreferrer">
                  Abrir WhatsApp
                </Button>
              ) : null}
            </div>

            <div className="crm-postventa-form-grid">
              <Form.Group className="crm-postventa-form-grid-span-2">
                <Form.Label>Resultado</Form.Label>
                <Form.Select value={form.resultado} onChange={(event) => setForm((prev) => ({ ...prev, resultado: event.target.value }))}>
                  <option value="">Seleccionar</option>
                  {allowedResults.map((item) => (
                    <option key={item} value={item}>{RESULT_LABELS[item] || item}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label>Duracion en segundos</Form.Label>
                <Form.Control
                  value={form.duracionSegundos}
                  onChange={(event) => setForm((prev) => ({ ...prev, duracionSegundos: event.target.value.replace(/[^\d]/g, "") }))}
                  placeholder="Solo si aplica"
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Evidencia</Form.Label>
                <Form.Control
                  value={form.evidencia}
                  onChange={(event) => setForm((prev) => ({ ...prev, evidencia: event.target.value }))}
                  placeholder={activeFlujo?.requiereEvidencia ? "Obligatoria" : "Opcional"}
                />
              </Form.Group>

              <Form.Group className="crm-postventa-form-grid-span-2">
                <Form.Label>Observacion</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={form.observacion}
                  onChange={(event) => setForm((prev) => ({ ...prev, observacion: event.target.value }))}
                  placeholder={activeFlujo?.requiereObservacion ? "Obligatoria" : "Opcional"}
                />
              </Form.Group>
            </div>

            {activeHistory.length ? (
              <div className="crm-postventa-zoco-detail-wide">
                <span className="crm-postventa-card-label">Ultimas gestiones</span>
                <ul className="crm-postventa-detail-lines">
                  {activeHistory.slice(0, 5).map((item) => (
                    <li key={item.id}>{`${formatDateTime(item.fechaHora)} · ${item.usuario} · ${item.estado}`}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="outline-secondary" onClick={closeGestionModal}>Cancelar</Button>
            <Button type="submit" variant="success" disabled={saving}>{saving ? "Guardando..." : "Registrar accion"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </section>
  );
};

export default ZocoCheckPanel;
